import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, SlidersHorizontal, ChevronUp, ChevronsDown } from "lucide-react";
import { BRANDS, PRICE_BUCKETS, FUELS, TRANSMISSIONS, BODY_TYPES } from "../data/india";

const YEARS = Array.from({ length: 16 }, (_, i) => String(new Date().getFullYear() - i));
const SORTS = [["", "Newest first"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"], ["km_asc", "Lowest KM"], ["year_desc", "Newest model"]];
const KM = [["10000", "Under 10,000 km"], ["30000", "Under 30,000 km"], ["50000", "Under 50,000 km"], ["75000", "Under 75,000 km"], ["100000", "Under 1,00,000 km"]];
const OWNERS = [["1", "1st owner"], ["2", "Up to 2nd owner"], ["3", "Up to 3rd owner"]];
const EMPTY = { seller_type: "", brand: "", price: "", fuel: "", transmission: "", body_type: "", min_year: "", min_price: "", max_price: "", max_km: "", max_owners: "", sort: "" };

const Field = ({ label, value, onChange, options, all, id }) => (
  <div>
    <label className="lux-label">{label}</label>
    <select className="lux-select" value={value} onChange={(e) => onChange(e.target.value)} data-testid={id}>
      <option value="">{all}</option>
      {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o}>{o}</option>))}
    </select>
  </div>
);

export const scrollToListings = () => document.getElementById("home-listings")?.scrollIntoView({ behavior: "smooth", block: "start" });

export default function HeroSearch() {
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [hidden, setHidden] = useState(true);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (h) => setHidden(h);

  const go = () => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && k !== "price" && p.set(k, v));
    if (f.price !== "" && !f.min_price && !f.max_price) {
      const b = PRICE_BUCKETS[f.price];
      if (b.min) p.set("min_price", b.min);
      if (b.max) p.set("max_price", b.max);
    }
    navigate(`/cars?${p.toString()}`);
  };
  const tabs = [["", "All Cars"], ["private", "Private Owners"], ["dealer", "Dealer Stores"]];

  if (hidden) return (
    <div className="flex flex-wrap gap-2 rise">
      <button onClick={() => toggle(false)} className="btn-outline !py-1.5 !px-3 text-xs" data-testid="show-filters-btn"><SlidersHorizontal className="w-3.5 h-3.5" />Show Filters</button>
      <button onClick={scrollToListings} className="btn-outline !py-1.5 !px-3 text-xs" data-testid="scroll-to-listings-btn"><ChevronsDown className="w-3.5 h-3.5" />Scroll to cars</button>
    </div>
  );

  return (
    <div className="rise" style={{ animationDelay: "200ms" }}>
      <div className="bg-black/60 backdrop-blur-xl border border-[#D4AF37]/30 rounded-2xl p-4 shadow-2xl" data-testid="hero-search">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex gap-1.5 flex-wrap">
            {tabs.map(([v, l]) => (
              <button key={v} onClick={() => set("seller_type")(v)} data-testid={`seller-type-tab-${v || "all"}`}
                className={`px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors ${f.seller_type === v ? "text-black" : "text-gray-300 border border-white/10 hover:border-[#D4AF37]/50"}`}
                style={f.seller_type === v ? { background: "var(--gold-grad)" } : {}}>{l}</button>
            ))}
          </div>
          <button onClick={() => toggle(true)} className="text-[11px] text-gray-400 hover:text-[#E5C158] flex items-center gap-1" data-testid="hide-filters-btn"><ChevronUp className="w-3.5 h-3.5" />Hide filters</button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5 items-end">
          <Field label="Brand" value={f.brand} onChange={set("brand")} options={BRANDS} all="Any brand" id="hero-brand-select" />
          <Field label="Budget" value={f.price} onChange={set("price")} options={PRICE_BUCKETS.map((b, i) => [String(i), b.label])} all="Any budget" id="hero-price-select" />
          <Field label="Fuel type" value={f.fuel} onChange={set("fuel")} options={FUELS} all="Any fuel" id="hero-fuel-select" />
          <Field label="Transmission" value={f.transmission} onChange={set("transmission")} options={TRANSMISSIONS} all="Any" id="hero-transmission-select" />
          <Field label="Body type" value={f.body_type} onChange={set("body_type")} options={BODY_TYPES} all="Any body" id="hero-body-select" />
          <Field label="Year from" value={f.min_year} onChange={set("min_year")} options={YEARS} all="Any year" id="hero-year-select" />
          <div><label className="lux-label">Min price ₹</label><input className="lux-input" type="number" min="0" value={f.min_price} onChange={(e) => set("min_price")(e.target.value)} placeholder="0" data-testid="hero-min-price" /></div>
          <div><label className="lux-label">Max price ₹</label><input className="lux-input" type="number" min="0" value={f.max_price} onChange={(e) => set("max_price")(e.target.value)} placeholder="Any" data-testid="hero-max-price" /></div>
          <Field label="KM driven" value={f.max_km} onChange={set("max_km")} options={KM} all="Any km" id="hero-km-select" />
          <Field label="Owner type" value={f.max_owners} onChange={set("max_owners")} options={OWNERS} all="Any owner" id="hero-owner-select" />
          <Field label="Sort by" value={f.sort} onChange={set("sort")} options={SORTS.slice(1)} all={SORTS[0][1]} id="hero-sort-select" />
          <button onClick={go} className="btn-gold h-[36px]" data-testid="hero-search-button"><Search className="w-3.5 h-3.5" /> Search</button>
        </div>
      </div>
      <div className="flex justify-center mt-3">
        <button onClick={scrollToListings} className="scroll-hint flex flex-col items-center text-[10px] uppercase tracking-widest text-gray-400 hover:text-[#E5C158] transition-colors" data-testid="scroll-down-btn">
          Scroll down<ChevronsDown className="w-5 h-5 animate-bounce text-[#D4AF37]" />
        </button>
      </div>
    </div>
  );
}
